import Mustache from 'mustache';

const SELECTABLE_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'password', 'textarea']);

export class TemplateRenderer {
    private childElement?: HTMLElement;

    private focusedInputState: {
        name?: string;
        id?: string;
        value: string;
        cursor: number;
    } | null = null;

    constructor(
        private owner: any,
        private template: string,
        private allowedEvents: string[] = ['click', 'input', 'change', 'focus', 'blur']
    ) {}

    public render(container: HTMLElement, data: Record<string, unknown>): void {
        this.captureFocusedInput();

        this.destroy();

        let html = Mustache.render(this.template, data);

        if (!html.trim()) {
            return;
        }

        let childDiv = document.createElement('div');
        childDiv.innerHTML = html;

        this.bindEvents(childDiv);

        container.appendChild(childDiv);
        this.childElement = childDiv;

        this.restoreFocusedInput();
    }

    private destroy(): void {
        this.childElement?.remove();
        this.childElement = undefined;
    }

    private bindEvents(root: HTMLElement): void {
        let elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
        for (let element of elements) {
            for (let eventName of this.allowedEvents) {
                let attribute = element.getAttribute(`(${eventName})`);

                if (!attribute) {
                    continue;
                }

                let match = attribute.match(/^([a-zA-Z0-9_]+)\((.*)\)$/);

                if (match) {
                    let method = this.owner[match[1]];

                    if (typeof method === 'function') {
                        element.addEventListener(eventName, event => {
                            let params = this.resolveParameters(match[2], element, event);

                            method.apply(this.owner, params);
                        });
                    }
                }

                element.removeAttribute(`(${eventName})`);
            }
        }
    }

    private resolveParameters(paramsString: string, element: HTMLElement, event: Event): any[] {
        if (!paramsString.trim()) {
            return [];
        }

        return paramsString.split(',').map(param => {
            let trimmed = param.trim();

            if (trimmed === '$event') {
                return event;
            }

            if (/^'.*'$/.test(trimmed) || /^".*"$/.test(trimmed)) {
                return trimmed.slice(1, -1);
            }

            if (!isNaN(Number(trimmed))) {
                return Number(trimmed);
            }

            if (trimmed in this.owner) {
                return this.owner[trimmed];
            }

            return undefined;
        });
    }

    private getDeepActiveElement(): Element | null {
        let active = document.activeElement;

        while (active?.shadowRoot?.activeElement) {
            active = active.shadowRoot.activeElement;
        }

        return active;
    }

    private captureFocusedInput(): void {
        let active = this.getDeepActiveElement() as HTMLInputElement | HTMLTextAreaElement;
        if (!active || !this.childElement?.contains(active)) {
            this.focusedInputState = null;
            return;
        }

        this.focusedInputState = {
            name: active.name,
            id: active.id,
            value: active.value,
            cursor: SELECTABLE_INPUT_TYPES.has(active.type) ? active.selectionStart ?? 0 : 0,
        };
    }

    private restoreFocusedInput(): void {
        if (!this.focusedInputState) {
            return;
        }

        let selector = this.focusedInputState.id ? `#${this.focusedInputState.id}` : this.focusedInputState.name ? `[name="${this.focusedInputState.name}"]` : null;
        if (!selector) {
            return;
        }

        let input = this.childElement?.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);
        if (!input) {
            return;
        }

        input.value = this.focusedInputState.value;
        input.focus();

        if (SELECTABLE_INPUT_TYPES.has(input.type)) {
            input.setSelectionRange(this.focusedInputState.cursor, this.focusedInputState.cursor);
        }
    }
}
